import React, { useState } from 'react';
import { ArrowLeftIcon } from './Icons';
import { StarRating, BettrScore } from './HelperComponents';

const ProductDetail = ({ product, onBack }) => {
    const parseAdditives = (additivesString) => {
        if (!additivesString || additivesString.trim() === '') return [];

        try {
            // Split by semicolon to get individual dictionary strings
            const dictStrings = additivesString.split(';').filter(s => s.trim());

            return dictStrings.map(dictStr => {
                // Remove curly braces and split by comma
                const cleanStr = dictStr.replace(/[{}]/g, '');
                const pairs = cleanStr.split(', ');

                const additive = {};
                pairs.forEach(pair => {
                    const colonIndex = pair.indexOf(': ');
                    if (colonIndex !== -1) {
                        const key = pair.substring(0, colonIndex).trim();
                        const value = pair.substring(colonIndex + 2).trim();

                        // Remove quotes from key and value
                        const cleanKey = key.replace(/['"]/g, '');
                        let cleanValue = value.replace(/['"]/g, '');

                        // Handle None values
                        if (cleanValue === 'None') cleanValue = null;

                        additive[cleanKey] = cleanValue;
                    }
                });

                return additive;
            }).filter(additive => additive.ingredient_name); // Only include valid additives
        } catch (error) {
            console.error('Error parsing additives:', error);
            return [];
        }
    };

    const additives = parseAdditives(product.ins_e_ingredients_list);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    const handleImageClick = (index) => {
        setCurrentImageIndex(index);
    };

    return (
        <>
            <button className="back-btn-fixed" onClick={onBack}>
                <ArrowLeftIcon />
                Back to Products
            </button>
            <div className="product-detail">
                <div className="product-detail-header">
                    <div className="product-title-section">
                        <h1 className="product-title">{product.product_name}</h1>
                        <div className="product-meta">
                            <span className="brand-name">{product.extracted_brand_name}</span>
                            {product.extracted_manufacturer_name && (
                                <span className="manufacturer-name">by {product.extracted_manufacturer_name}</span>
                            )}
                        </div>
                    </div>
                </div>

            <div className="product-detail-content">
                <div className="product-image-section">
                    <div className="main-image-container">
                        <img
                            src={product.image_urls && product.image_urls[currentImageIndex] ? product.image_urls[currentImageIndex] : 'https://via.placeholder.com/400x400/f0f0f0/666666?text=No+Image'}
                            alt={product.product_name}
                            className="product-detail-image"
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src='https://via.placeholder.com/400x400/f0f0f0/666666?text=No+Image';
                            }}
                        />
                    </div>
                    {product.image_urls && product.image_urls.length > 1 && (
                        <div className="thumbnail-grid">
                            {product.image_urls.map((imageUrl, index) => (
                                <div
                                    key={index}
                                    className={`thumbnail ${index === currentImageIndex ? 'active' : ''}`}
                                    onClick={() => handleImageClick(index)}
                                >
                                    <img
                                        src={imageUrl}
                                        alt={`${product.product_name} ${index + 1}`}
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src='https://via.placeholder.com/60x60/f0f0f0/666666?text=No+Img';
                                        }}
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="product-info-section">
                        {/* Product Information */}
                        <div className="info-card">
                            <h3 className="card-title">📦 Product Information</h3>
                            <div className="nutritional-grid">
                                <div className="nutrient-item">
                                    <span className="nutrient-label">Brand</span>
                                    <span className="nutrient-value">{product.extracted_brand_name || 'N/A'}</span>
                                </div>
                                {/* <div className="nutrient-item">
                                    <span className="nutrient-label">Category</span>
                                    <span className="nutrient-value">{product.category || 'N/A'}</span>
                                </div> */}
                                <div className="nutrient-item">
                                    <span className="nutrient-label">Manufacturer</span>
                                    <span className="nutrient-value">{product.extracted_manufacturer_name || 'N/A'}</span>
                                </div>
                                <div className="nutrient-item">
                                    <span className="nutrient-label">Country of Origin</span>
                                    <span className="nutrient-value">{product.extracted_country_of_origin || 'N/A'}</span>
                                </div>
                                <div className="nutrient-item">
                                    <span className="nutrient-label">Item Part Number</span>
                                    <span className="nutrient-value">{product.extracted_item_part_number || 'N/A'}</span>
                                </div>
                                <div className="nutrient-item">
                                    <span className="nutrient-label">Package Dimensions</span>
                                    <span className="nutrient-value">{product.extracted_package_dimensions || 'N/A'}</span>
                                </div>
                                <div className="nutrient-item">
                                    <span className="nutrient-label">Item Weight</span>
                                    <span className="nutrient-value">{product.extracted_item_weight || 'N/A'}</span>
                                </div>
                                <div className="nutrient-item">
                                    <span className="nutrient-label">Net Quantity</span>
                                    <span className="nutrient-value">{product.extracted_net_quantity || 'N/A'}</span>
                                </div>
                                <div className="nutrient-item">
                                    <span className="nutrient-label">Generic Name</span>
                                    <span className="nutrient-value">{product.extracted_generic_name || 'N/A'}</span>
                                </div>
                            </div>
                        </div>

                        {/* Nutritional Information */}
                        <div className="info-card">
                            <h3 className="card-title">🧪 Nutritional Information (per 100g/ml)</h3>
                            <div className="nutritional-grid">
                                <div className="nutrient-item">
                                    <span className="nutrient-label">Energy</span>
                                    <span className="nutrient-value">{product.energy_kcal || 'N/A'} kcal</span>
                                </div>
                                <div className="nutrient-item">
                                    <span className="nutrient-label">Protein</span>
                                    <span className="nutrient-value">{product.protein_g || 'N/A'}g</span>
                                </div>
                                <div className="nutrient-item">
                                    <span className="nutrient-label">Total Sugars</span>
                                    <span className="nutrient-value">{product.total_sugars_g || 'N/A'}g</span>
                                </div>
                                <div className="nutrient-item">
                                    <span className="nutrient-label">Saturated Fat</span>
                                    <span className="nutrient-value">{product.saturated_fat_g || 'N/A'}g</span>
                                </div>
                                <div className="nutrient-item">
                                    <span className="nutrient-label">Sodium</span>
                                    <span className="nutrient-value">{product.sodium_mg || 'N/A'}mg</span>
                                </div>
                                <div className="nutrient-item">
                                    <span className="nutrient-label">Fibre</span>
                                    <span className="nutrient-value">{product.fibre_g || 'N/A'}g</span>
                                </div>
                            </div>
                        </div>

                        {/* Additives */}
                        <div className="info-card">
                            <h3 className="card-title">🧪 Additives (INS/E Numbers)</h3>
                            {additives.length > 0 ? (
                                <div className="additives-list">
                                    {additives.map((additive, index) => (
                                        <div key={index} className="additive-item">
                                            <div className="additive-info">
                                                <span className="additive-name">{additive.ingredient_name}</span>
                                                {additive.ins_e_number && (
                                                    <span className="additive-number">({additive.ins_e_number})</span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="no-data">No additives found</p>
                            )}
                        </div>

                        {/* Ingredients */}
                        <div className="info-card">
                            <h3 className="card-title">🥗 Ingredients</h3>
                            <div className="ingredients-content">
                                {product.ingredient_list ? (
                                    <div className="ingredients-list">
                                        {(() => {
                                            // Parse and format ingredients
                                            let ingredients = [];
                                            if (Array.isArray(product.ingredient_list)) {
                                                ingredients = product.ingredient_list;
                                            } else if (typeof product.ingredient_list === 'string') {
                                                try {
                                                    const parsed = JSON.parse(product.ingredient_list);
                                                    if (Array.isArray(parsed)) {
                                                        ingredients = parsed;
                                                    } else {
                                                        ingredients = product.ingredient_list.split(';').map(s => s.trim()).filter(Boolean);
                                                    }
                                                } catch {
                                                    ingredients = product.ingredient_list.split(';').map(s => s.trim()).filter(Boolean);
                                                }
                                            }

                                            return ingredients.map((ingredient, index) => (
                                                <span key={index} className="ingredient-item">
                                                    {ingredient.trim()}
                                                    {index < ingredients.length - 1 && ', '}
                                                </span>
                                            ));
                                        })()}
                                    </div>
                                ) : (
                                    <p className="no-data">No ingredient information available</p>
                                )}
                            </div>
                        </div>

                        {/* Key Concerns */}
                        <div className="info-card">
                            <h3 className="card-title">😟 Key Concerns</h3>
                            <div className="key-concerns">
                                {product.key_concerns_with_nutrients && <p><strong>Nutrients:</strong> {product.key_concerns_with_nutrients}</p>}
                                {product.key_concerns_with_ingredients && <p><strong>Ingredients:</strong> {product.key_concerns_with_ingredients}</p>}
                                {!product.key_concerns_with_nutrients && !product.key_concerns_with_ingredients && <p className="no-data">No key concerns identified.</p>}
                            </div>
                        </div>

                        {/* Marketing */}
                        <div className="info-card">
                            <h3 className="card-title">📣 Marketing</h3>
                            <div className="marketing-info">
                                {product.marketing_highlights && (
                                    <div className="marketing-section">
                                        <h4 className="marketing-subtitle">Highlights</h4>
                                        <ul className="marketing-list">
                                            {product.marketing_highlights.split(';').map((highlight, index) => (
                                                <li key={index} className="marketing-item">
                                                    {highlight.trim()}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {product.promotional_messages && (
                                    <div className="marketing-section">
                                        <h4 className="marketing-subtitle">Promotional Messages</h4>
                                        <ul className="marketing-list">
                                            {product.promotional_messages.split(';').map((message, index) => (
                                                <li key={index} className="marketing-item">
                                                    {message.trim()}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {product.slogans_and_taglines && (
                                    <div className="marketing-section">
                                        <h4 className="marketing-subtitle">Slogans/Taglines</h4>
                                        <ul className="marketing-list">
                                            {product.slogans_and_taglines.split(';').map((slogan, index) => (
                                                <li key={index} className="marketing-item">
                                                    {slogan.trim()}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {!product.marketing_highlights && !product.promotional_messages && !product.slogans_and_taglines && <p className="no-data">No marketing information available.</p>}
                            </div>
                        </div>

                        {/* Benefit Claims */}
                        <div className="info-card">
                            <h3 className="card-title">👍 Benefit Claims</h3>
                            <div className="benefit-claims">
                                {product.benefit_claims ? (
                                    <ul className="benefit-list">
                                        {product.benefit_claims.split(';').map((claim, index) => (
                                            <li key={index} className="benefit-item">
                                                {claim.trim()}
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="no-data">No benefit claims available.</p>
                                )}
                            </div>
                        </div>

                        {/* Allergens */}
                        <div className="info-card">
                            <h3 className="card-title">🤧 Allergen Information</h3>
                            <div className="allergen-info">
                                {product.allergen_info ? <p>{product.allergen_info}</p> : <p className="no-data">No allergen information available.</p>}
                            </div>
                        </div>

                        {/* Positives */}
                        <div className="info-card">
                            <h3 className="card-title">😊 Positives</h3>
                            <div className="positives-info">
                                {product.primary_product_positives && <p><strong>Primary:</strong> {product.primary_product_positives}</p>}
                                {product.secondary_product_positives && <p><strong>Secondary:</strong> {product.secondary_product_positives}</p>}
                                {!product.primary_product_positives && !product.secondary_product_positives && <p className="no-data">No positives identified.</p>}
                            </div>
                        </div>

                        {/* Scores */}
                        {/* <div className="scores-section">
                            {product.final_star_rating > 0 && (
                                <div className="score-card">
                                    <h4>Customer Rating</h4>
                                    <StarRating rating={product.final_star_rating} />
                                </div>
                            )}
                            {product.bettr_score > 0 && (
                                <div className="score-card">
                                    <h4>Bettr Score</h4>
                                    <BettrScore score={product.bettr_score} />
                                </div>
                            )}
                            {product.ingredient_profile_score_json && (
                                <div className="score-card">
                                    <h4>Ingredient Profile Score</h4>
                                    <pre>{JSON.stringify(product.ingredient_profile_score_json, null, 2)}</pre>
                                </div>
                            )}
                        </div> */}
                </div>
            </div>
        </div>
        </>
    );
};

export default ProductDetail;
